import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { fetchCollections, updateCollectionStatus } from '../api/client';

const STATUS_TABS = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'archived', label: 'Archived' },
  { key: 'all', label: 'All' },
];

const STATUS_STYLES = {
  active: 'bg-success/15 text-success border border-success/30',
  inactive: 'bg-surface text-text-secondary border border-ink/20',
  archived: 'bg-alert/10 text-alert border border-alert/30',
};

function formatDuration(seconds) {
  if (!seconds) return '';
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

/**
 * Extract the first email step from a wing's steps array for display purposes.
 */
function extractWingEmail(wing) {
  const steps = wing.steps || [];
  let delayDuration = 0;
  let delayLabel = '';

  for (const step of steps) {
    if (step.type === 'wait') {
      delayDuration = step.duration;
      delayLabel = step.label || formatDuration(step.duration);
    } else if (step.type === 'email') {
      return {
        stepId: step.id,
        stepNumber: step.stepNumber,
        name: step.name || step.templateId,
        templateId: step.templateId,
        delayBefore: delayDuration,
        delayLabel,
      };
    }
  }
  return null;
}

/**
 * Group wing records by collectionNumber into collection groups.
 * Each group has a collectionNumber, collectionName, and array of wings.
 */
function groupByCollection(wings) {
  const groups = new Map();

  for (const wing of wings) {
    const num = wing.collectionNumber || 0;
    if (!groups.has(num)) {
      groups.set(num, {
        collectionNumber: num,
        collectionName: wing.collectionName || wing.name,
        wings: [],
      });
    }
    groups.get(num).wings.push(wing);
  }

  // Sort groups by collectionNumber, wings within each group by their ID
  return Array.from(groups.values())
    .sort((a, b) => a.collectionNumber - b.collectionNumber)
    .map(group => ({
      ...group,
      wings: group.wings.sort((a, b) => a.collectionId.localeCompare(b.collectionId)),
    }));
}

export default function Wings() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [togglingId, setTogglingId] = useState(null);

  const loadCollections = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const result = await fetchCollections(token);
      setCollections(result.collections || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    loadCollections();
    const interval = setInterval(loadCollections, 30000);
    return () => clearInterval(interval);
  }, [loadCollections]);

  const handleToggle = async (e, wing) => {
    e.stopPropagation();
    const newStatus = wing.status === 'active' ? 'inactive' : 'active';
    setTogglingId(wing.collectionId);
    try {
      const token = await getAccessTokenSilently();
      await updateCollectionStatus(token, wing.collectionId, newStatus);
      setCollections(prev => prev.map(c =>
        c.collectionId === wing.collectionId ? { ...c, status: newStatus } : c
      ));
    } catch (err) {
      setError(err.message);
    } finally {
      setTogglingId(null);
    }
  };

  // Filter wings by status tab
  const filtered = activeTab === 'all'
    ? collections
    : collections.filter(c => c.status === activeTab);

  // Group filtered wings into collections
  const collectionGroups = groupByCollection(filtered);

  // Counts for tabs
  const totalWings = collections.length;
  const wingsByStatus = (status) => collections.filter(c => c.status === status).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-text-secondary">Loading wings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-alert/10 border-2 border-alert rounded-xl p-6 shadow-brutal">
        <p className="text-alert font-semibold">Failed to load wings</p>
        <p className="text-text-secondary text-sm mt-1">{error}</p>
        <button onClick={loadCollections} className="mt-3 text-sm text-primary font-medium hover:underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-extrabold text-text-primary">Wings</h2>
          <p className="text-sm text-text-secondary mt-0.5">
            {totalWings} wings across {groupByCollection(collections).length} collections
          </p>
        </div>
        <button
          onClick={() => navigate('/wings/new')}
          className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-xl border-2 border-ink shadow-brutal hover:-translate-y-0.5 hover:shadow-brutal-lg active:translate-y-0.5 active:shadow-brutal-sm transition-all duration-200"
        >
          Create Wing
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-surface border-2 border-ink/20 rounded-xl p-1 w-fit">
        {STATUS_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
              activeTab === tab.key
                ? 'bg-primary text-white shadow-brutal-sm'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab.label}
            <span className="ml-1.5 text-xs opacity-60">
              {tab.key === 'all'
                ? totalWings
                : wingsByStatus(tab.key)}
            </span>
          </button>
        ))}
      </div>

      {/* Wings grouped by collection */}
      {collectionGroups.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-text-secondary">
            No {activeTab === 'all' ? '' : activeTab} wings found.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {collectionGroups.map(group => (
            <div key={group.collectionNumber} className="space-y-2">
              {/* Collection group header */}
              <div className="flex items-center gap-3 px-2">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/20 text-primary text-xs font-bold border border-primary/30">
                  {group.collectionNumber}
                </span>
                <h3 className="text-sm font-bold text-text-primary">
                  {group.collectionName}
                </h3>
                <span className="text-xs text-text-secondary">
                  {group.wings.length} {group.wings.length === 1 ? 'wing' : 'wings'}
                </span>
              </div>

              {/* Wing rows */}
              <div className="bg-surface rounded-xl border-2 border-ink shadow-brutal overflow-hidden">
                {group.wings.map((wing, idx) => {
                  const email = extractWingEmail(wing);
                  return (
                    <div
                      key={wing.collectionId}
                      className={`flex items-center gap-4 px-5 py-3 ${
                        idx > 0 ? 'border-t border-ink/10' : ''
                      }`}
                    >
                      {/* Wing toggle */}
                      {wing.status !== 'archived' && (
                        <div
                          onClick={(e) => handleToggle(e, wing)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer border-2 shrink-0 ${
                            wing.status === 'active' ? 'bg-primary border-ink' : 'bg-ink/20 border-ink/30'
                          } ${togglingId === wing.collectionId ? 'opacity-50' : ''}`}
                        >
                          <span
                            className={`inline-block h-3 w-3 rounded-full bg-white transition-transform ${
                              wing.status === 'active' ? 'translate-x-4' : 'translate-x-0.5'
                            }`}
                          />
                        </div>
                      )}

                      {/* Clickable wing content */}
                      <button
                        onClick={() => navigate(`/wings/${wing.collectionId}`)}
                        className="flex-1 flex items-center gap-4 text-left hover:bg-background/60 transition-colors rounded-lg -mx-2 px-2 py-1"
                      >
                        {/* Wing info */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-text-primary truncate">
                              {wing.name}
                            </p>
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_STYLES[wing.status] || STATUS_STYLES.inactive}`}>
                              {wing.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs font-mono text-text-secondary bg-background px-1.5 py-0.5 rounded border border-ink/10">
                              {wing.triggerEvent}
                            </span>
                            {email?.templateId && (
                              <span className="text-xs text-text-secondary truncate">
                                {email.templateId}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Delay before */}
                        {email?.delayLabel && (
                          <span className="text-xs text-text-secondary bg-background px-2 py-0.5 rounded border border-ink/10 shrink-0">
                            after {email.delayLabel}
                          </span>
                        )}

                        {/* Chevron */}
                        <svg className="w-4 h-4 text-text-secondary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
